import { AddRounded, RemoveRounded } from "@mui/icons-material";
import { IconButton, TextField, TextFieldProps } from "@mui/material";
import { memo, useCallback, useEffect, useState } from "react";
import { useStyles } from "./styles";

export type NumberInputFieldProps = {
    height?: number,
    stepSize?: number,
    minValue?: number,
    maxValue?: number,
    showSteppers?: boolean,
    onValueChange: (value: number) => void
} & TextFieldProps

const snapToStep = (value: number, stepSize: number, minValue?: number, maxValue?: number) => {
    const min = minValue ?? (stepSize > 1 ? stepSize : undefined)
    let snapped = value

    if (snapped <= 0) {
        snapped = min ?? snapped
    } else if (stepSize > 1) {
        snapped = Math.max(Math.floor(snapped / stepSize) * stepSize, min ?? stepSize)
    }

    if (min !== undefined && snapped < min) {
        snapped = min
    }

    if (maxValue !== undefined && snapped > maxValue) {
        snapped = stepSize > 1
            ? Math.max(Math.floor(maxValue / stepSize) * stepSize, min ?? stepSize)
            : maxValue
    }

    return snapped
}

const NumberInputField: React.FC<NumberInputFieldProps> = ({height, stepSize = 1, minValue, maxValue, showSteppers = true, sx, onValueChange, onBlur, onFocus, onKeyDown, ...inputProps}) => {
    const { classes } = useStyles();
    let inputFieldProps = {...(inputProps || {})};

    const value = inputProps.value as number;
    const inputHeight = height ?? 24;
    const [focused, setFocused] = useState(false);
    const [displayValue, setDisplayValue] = useState(() => String(value ?? ''));

    useEffect(() => {
        if (!focused) {
            setDisplayValue(String(value ?? ''));
        }
    }, [value, focused]);

    if (showSteppers) {

        inputFieldProps = {
            ...inputFieldProps,
            InputProps: {
                ...(inputFieldProps.InputProps || {}),
                sx: {
                    ...(inputFieldProps.InputProps?.sx || {}),
                    px: 0.25,
                    py: 0.2,
                    height: inputHeight,
                    '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                        display: 'none'
                    },
                    '& input[type=number]': {
                        MozAppearance: 'textfield'
                    }
                },
                startAdornment: (
                    <IconButton
                        onClick={() => {
                            if (value === undefined) return
                            const newValue = value - stepSize
                            if (minValue !== undefined && newValue < minValue) return
                            onValueChange(newValue)
                        }}
                        size={'small'}
                        sx={{ p: 0 }}
                    >
                        <RemoveRounded className="trade-panel-edit-icons" />
                    </IconButton>
                ),
                endAdornment: (
                    <IconButton
                        onClick={() => {
                            if (value === undefined) return
                            const newValue = value + stepSize
                            if (maxValue !== undefined && newValue > maxValue) return
                            onValueChange(newValue)
                        }}
                        size={'small'}
                        sx={{ p: 0 }}
                    >
                        <AddRounded className="trade-panel-edit-icons" />
                    </IconButton>
                )
            }
        }
        }
    
    inputFieldProps.inputProps = {
        ...(inputFieldProps.inputProps || {}),
        sx: {
            ...(inputFieldProps.inputProps?.sx || {}),
            padding: '0 !important',
            textAlign: 'center',
            fontSize: 12,
            maxWidth: 40
        }
    }

    const onChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;
        setDisplayValue(newValue);
        if (newValue !== '' && !isNaN(Number(newValue))) {
            onValueChange(Number(newValue));
        }
    }, [onValueChange])

    const handleFocus = useCallback((event: React.FocusEvent<HTMLInputElement>) => {
        setFocused(true);
        onFocus?.(event);
    }, [onFocus])

    const handleBlur = useCallback((event: React.FocusEvent<HTMLInputElement>) => {
        setFocused(false);
        const rawValue = event.target.value
        const numValue = rawValue === '' || isNaN(Number(rawValue)) ? 0 : Number(rawValue)
        const snapped = snapToStep(numValue, stepSize, minValue, maxValue)

        setDisplayValue(String(snapped));
        if (snapped !== value) {
            onValueChange(snapped)
        }

        onBlur?.(event)
    }, [stepSize, minValue, maxValue, onValueChange, onBlur, value])

    const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            (event.target as HTMLInputElement)?.blur()
        }
        onKeyDown?.(event)
    }, [onKeyDown])

    const sxProps = {
        ...(sx || {}),
        paddingX: 0,
        width: 'fit-content'
    }
    return <TextField value={displayValue} {...inputFieldProps} sx={sxProps} classes={{root: classes.inputRoot}} onChange={onChange} onFocus={handleFocus} onBlur={handleBlur} onKeyDown={handleKeyDown} />;
}

export default memo(NumberInputField)